import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DashboardTwoToneIcon from "@mui/icons-material/DashboardTwoTone";
import MenuBookTwoToneIcon from "@mui/icons-material/MenuBookTwoTone";
import PersonAddAltTwoToneIcon from "@mui/icons-material/PersonAddAltTwoTone";
import PendingActionsSharpIcon from "@mui/icons-material/PendingActionsSharp";
import RestoreSharpIcon from "@mui/icons-material/RestoreSharp";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import { Box, Skeleton } from "@mui/material";
import { DataButton } from "../../Objects/interfaces/AppInterface";
import { isTeachrAccountActive } from "../../Utils/Function";
import { getActiveBrandConfig, getActivePrestationBrandConfig } from "../../config/environment.config";
import { getActionLabelsByBrand, getDashboardLabelsByBrand } from "../../Utils/Function";
import { DispatchClientParams } from "../../config/CLIENT_TYPE_CONFIG";
import useTailwindBrand from "../../Theme/useTailwindBrand";
import { draftsPath } from "../../App/BrouillonsV2/paths";
import NavigationLayout from "./NavigationLayout";
import AddStudentDialogTrigger, { AddStudentDialogHandle } from "./AddStudentDialogTrigger";
import TeachrInfoDialog, { TeachrInfoDialogHandle } from "./TeachrInfoDialog";

export interface BrandNavHeaderProps {
    component?: number;
    currentPage: number;
    isHere?: boolean;
    hasNextCourse?: boolean
    hasStudents?: boolean;
    params?: DispatchClientParams;
    handleNextCourse?: (() => void) | (() => Promise<void>);
    handlePendingCourse?: () => void;
    handleActiveCourse?: () => void;
    handleHistoric?: () => void;
    handleDisplayStudentLeft?: () => void;
    handleDisplayStudentRight?: () => void;
    handleCA?: () => void;
    setLoad?: (() => void) | any;
    data?: any;
    titleCA?: string;
    dataBtn1Custom?: DataButton[];
    dataBtn2Custom?: DataButton[];
    activeCourse?: any;
    pastCourse?: any;
    changeStart?: () => void
    changeAllBord?: () => void
    overallProgress?: number
    isLoading?:boolean
}

const BrandNavHeader: React.FC<BrandNavHeaderProps> = (props) => {
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const infoDialogRef = useRef<TeachrInfoDialogHandle>(null);
    const studentDialogRef = useRef<AddStudentDialogHandle>(null);
    const [value, setValue] = useState<number>(props.component ?? 1);
    const { classes } = useTailwindBrand();
    const navigate = useNavigate();
    const lastDay = moment().endOf('month').format("YYYY-MM-DD")
    const day = moment().format("YYYY-MM-DD")
    const conditionDay = lastDay === day
    const hasActive = isTeachrAccountActive(teachr);
    const hasIbanBic = !!(teachr?.iban && teachr?.bic);
    const hasShownInfos = teachr?.isShowInfos;
    const hasAddress = teachr?.address;
    const hasIban = teachr?.iban;

    const handleDeclareClick = () => {
        if (!hasShownInfos) return infoDialogRef.current?.open();
        if (!hasAddress || !hasIban) return infoDialogRef.current?.openParam();
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/${getActivePrestationBrandConfig()}/declarer`);
    };

    const isVisible = (condition?: boolean) => (condition ? '' : 'hidden');
    const getNavClass = (isActive: boolean) => isActive ? classes.navHeader : classes.navHeader2;

    const primaryActionButtons: DataButton[] = [
        {
            id: "onboarding",
            value: 7,
            handleChange: props.changeStart,
            icon: PersonAddAltTwoToneIcon,
            title: "📊 Démarrage",
            classCustom: isVisible(
                props.currentPage === 0 && (props.overallProgress ?? 0) < 100
            ),
        },
        {
            id: "all-bord",
            value: 11,
            handleChange: props.changeAllBord,
            icon: DashboardTwoToneIcon,
            title: "🎯 Vue globale",
            classCustom: isVisible(
                props.currentPage === 0 && (props.overallProgress ?? 0) < 100
            ),
        },
        {
            id: "drafts-courses",
            value: 10,
            handleChange: () => props.handleNextCourse && props.handleNextCourse(),
            icon: FeedOutlinedIcon,
            title: getActionLabelsByBrand().thirdryActionLabel,
            classCustom: isVisible(props.currentPage === 0 && props.hasNextCourse && !conditionDay) + " " + getNavClass(props.component === 3),
        },
        {
            id: "declare",
            value: 1,
            handleChange: handleDeclareClick,
            icon: MenuBookTwoToneIcon,
            title: getActionLabelsByBrand().primaryActionLabel,
            classCustom: isVisible(
                (props.currentPage === 1) && props?.hasStudents && hasActive && hasIbanBic
            ),
        },
        {
            id: "add-user",
            value: 2,
            handleChange: () => studentDialogRef.current?.open(),
            icon: PersonAddAltTwoToneIcon,
            title: getActionLabelsByBrand().secondaryActionLabel,
            classCustom: isVisible(props.currentPage === 2),
        },
    ];

    const dashboardNavigationButtons: DataButton[] = [
        {
            id: "upcoming",
            value: 9,
            handleChange: props.handlePendingCourse,
            icon: PendingActionsSharpIcon,
            title: getDashboardLabelsByBrand().upcoming,
            classCustom: isVisible(
                props.currentPage !== 0 && props.currentPage !== 2
            ) + " " + getNavClass(props.component === 9),
        },
        {
            id: "current-month",
            value: 1,
            handleChange: props.handleActiveCourse,
            icon: PendingActionsSharpIcon,
            title: getDashboardLabelsByBrand().currentMonth,
            classCustom: isVisible(
                props.currentPage !== 0 && props.currentPage !== 2
            ) + " " + getNavClass(props.component === 1),
        },
        {
            id: "history",
            value: 2,
            handleChange: props.handleHistoric,
            icon: RestoreSharpIcon,
            title: getDashboardLabelsByBrand().history,
            classCustom: isVisible(
                props.currentPage !== 0 && props.currentPage !== 2
            ) + " " + getNavClass(props.component === 2),
        },
        {
            id: "drafts",
            value: 3,
            handleChange: props.handleNextCourse,
            icon: FeedOutlinedIcon,
            title: getDashboardLabelsByBrand().drafts,
            classCustom: isVisible(props.currentPage === 1 && props.hasNextCourse && !conditionDay
            ) + " " + getNavClass(props.component === 3),
        },
        {
            // Brouillons v2 : nouvelle liste en page (maquettes 1g, 2g), à côté de l'onglet existant
            id: "drafts-v2",
            value: 12,
            handleChange: () => navigate(draftsPath()),
            icon: FeedOutlinedIcon,
            title: "✨ Brouillons, nouvelle version",
            classCustom: isVisible(props.currentPage === 1 && props.hasNextCourse) + " " + getNavClass(false),
        },
        {
            id: "active-users",
            value: 5,
            handleChange: props.handleDisplayStudentLeft,
            icon: RestoreSharpIcon,
            title: getDashboardLabelsByBrand().activeUsers,
            classCustom:
                isVisible(props.currentPage === 2) +
                " " +
                getNavClass(props.component === 5) +
                " lg:px-10",
        },
        {
            id: "archived-users",
            value: 6,
            handleChange: props.handleDisplayStudentRight,
            icon: RestoreSharpIcon,
            title: getDashboardLabelsByBrand().archivedUsers,
            classCustom:
                isVisible(props.currentPage === 2) +
                " " +
                getNavClass(props.component === 6) +
                " lg:px-10",
        },
    ];
    const getActiveIdFromValue = (v: number): string | undefined =>
        dashboardNavigationButtons.find((b) => b.value === v)?.id ??
        primaryActionButtons.find((b) => b.value === v)?.id ??
        dashboardNavigationButtons[0]?.id ??
        primaryActionButtons[0]?.id;
    if (props.isLoading) {
        return (
            <Box className="flex gap-x-2 w-full lg:w-1/2">
                <Skeleton animation="wave" className="w-full h-6" />
                <Skeleton animation="wave" className="w-full h-6" />
                <Skeleton animation="wave" className="w-full h-6" />
                <Skeleton animation="wave" className="w-full h-6" />
            </Box>
        );
    }
    return (
        <>
            <AddStudentDialogTrigger
                ref={studentDialogRef}
                teachr={teachr}
                // params={props.params}
            />
            <TeachrInfoDialog
                ref={infoDialogRef}
                teachr={teachr}
            />
            <NavigationLayout
                dataBtn1={primaryActionButtons}
                dataBtn2={dashboardNavigationButtons}
                classes={classes}
                isHere={props.isHere}
                activeId={getActiveIdFromValue(value)}
                onActiveIdChange={(id) => {
                    const btn = [...dashboardNavigationButtons, ...primaryActionButtons].find((b) => b.id === id);
                    if (btn != null) setValue(btn.value);
                }}
                value={value}
                setValue={setValue}
            />
        </>
    );
};

export default BrandNavHeader;